import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';

interface InputProps {
  id: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number';
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
  showPasswordToggle?: boolean;
}

const Input: React.FC<InputProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  error,
  disabled = false,
  readOnly = false,
  required = false,
  className = '',
  showPasswordToggle = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const inputType = type === 'password' && showPasswordToggle 
    ? (showPassword ? 'text' : 'password')
    : type;

  const baseClasses = `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200 ${className}`;
  const errorClasses = error ? 'p-invalid border-red-500' : '';
  const disabledClasses = disabled ? 'bg-gray-100 cursor-not-allowed' : '';
  const readOnlyClasses = readOnly ? 'bg-gray-50 cursor-default' : '';

  return (
    <div className="space-y-3">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <InputText
          id={id}
          type={inputType}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`${baseClasses} ${errorClasses} ${disabledClasses} ${readOnlyClasses}`}
          disabled={disabled}
          readOnly={readOnly}
        />
        
        {type === 'password' && showPasswordToggle && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
            disabled={disabled}
          >
            <i className={`pi ${showPassword ? 'pi-eye-slash' : 'pi-eye'}`}></i>
          </button>
        )}
      </div>
      
      {error && (
        <small className="p-error text-red-600 text-sm mt-1 block">{error}</small>
      )}
    </div>
  );
};

export default Input; 